import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, MENU } from "@/data/site";

const MENU_ITEMS = MENU.flatMap((section) =>
  section.items.map((item) => ({ ...item, category: section.category }))
);

const normalizeImageUrl = (url = "") => url.split("?")[0].replace(/\\/g, "/");

const getMenuItem = (img) => {
  if (img.menuItemName) {
    return MENU_ITEMS.find((item) => item.name === img.menuItemName) || null;
  }

  const galleryImage = normalizeImageUrl(img.url);
  return (
    MENU_ITEMS.find((item) => normalizeImageUrl(item.image) === galleryImage) || null
  );
};

// Masonry-ish gallery grid with a full-screen lightbox.
export default function GalleryGrid({ images = GALLERY, limit }) {
  const shots = limit ? images.slice(0, limit) : images;
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(
    () => setIndex((i) => (i + 1) % shots.length),
    [shots.length]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + shots.length) % shots.length),
    [shots.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, next, prev]);

  return (
    <>
      <div
        className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[240px] gap-2 md:gap-3"
        data-testid="gallery-grid"
      >
        {shots.map((img, i) => {
          const menuItem = getMenuItem(img);

          return (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`group relative overflow-hidden bg-brand-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${
                img.span || ""
              }`}
              data-testid={`gallery-item-${i}`}
              aria-label={
                menuItem
                  ? `View ${menuItem.name}`
                  : `Open image: ${img.alt}`
              }
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 via-brand-ink/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {menuItem && (
                <span className="absolute inset-x-0 bottom-0 translate-y-0 p-4 md:p-5 text-left opacity-100 md:translate-y-2 md:opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
                  <span className="block font-display text-lg md:text-xl leading-tight text-white">
                    {menuItem.name}
                  </span>
                  <span className="mt-1 block text-xs md:text-sm leading-relaxed text-white/75">
                    {menuItem.desc}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-ink/95 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="lightbox"
          >
            <button
              className="absolute top-5 right-5 h-12 w-12 inline-flex items-center justify-center rounded-full border border-white/25 text-white hover:bg-white hover:text-brand-ink transition-colors"
              onClick={close}
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X size={22} />
            </button>
            <button
              className="absolute left-4 md:left-8 h-12 w-12 inline-flex items-center justify-center rounded-full border border-white/25 text-white hover:bg-white hover:text-brand-ink transition-colors"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous image"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <div
              className="relative max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                key={index}
                src={shots[index].url}
                alt={shots[index].alt}
                className="max-h-[82vh] max-w-[90vw] object-contain shadow-2xl"
                initial={{ scale: 0.94, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              />
              {getMenuItem(shots[index]) && (
                <motion.div
                  key={`caption-${index}`}
                  className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/55 to-transparent px-5 pb-5 pt-14 text-left md:px-7 md:pb-7"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25 }}
                >
                  <p className="font-display text-xl md:text-2xl text-white leading-tight">
                    {getMenuItem(shots[index]).name}
                  </p>
                  <p className="mt-1 text-sm text-white/75 leading-relaxed">
                    {getMenuItem(shots[index]).desc}
                  </p>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/50">
                    {getMenuItem(shots[index]).category}
                  </p>
                </motion.div>
              )}
            </div>
            <button
              className="absolute right-4 md:right-8 h-12 w-12 inline-flex items-center justify-center rounded-full border border-white/25 text-white hover:bg-white hover:text-brand-ink transition-colors"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next image"
              data-testid="lightbox-next"
            >
              <ChevronRight size={24} />
            </button>
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm tracking-wide">
              {index + 1} / {shots.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
